import { Observable, of, tap } from 'rxjs';

type CacheResultType = 'observable' | 'plain';
interface CacheResult {
  value: unknown;
  expiresIn: number;
}
interface CacheOptions {
  ttlMs: number;
  type: CacheResultType;
}

type CacheableResource = Record<symbol, Map<string, CacheResult>>;
export function removeCache(cacheableResource: CacheableResource | object) {
  const symbols = Object.getOwnPropertySymbols(cacheableResource);

  for (const cacheableSymbol of symbols) {
    const strCacehableSymbol = cacheableSymbol.toString();
    let cacheKey = strCacehableSymbol.replace('Symbol', '');
    cacheKey = cacheKey.substring(1, cacheKey.length - 1);

    if (cacheableSymbol in cacheableResource) {
      const cacheObject: Map<string, CacheResult> = ((cacheableResource as CacheableResource)[cacheableSymbol] ??=
        new Map());
      if (cacheObject.has(cacheKey)) {
        cacheObject.delete(cacheKey);
      }
    }
  }
}

export function cacheDecorator(opts: CacheOptions = { ttlMs: 1000 * 60 * 1, type: 'observable' }) {
  const { ttlMs, type } = opts;

  return function (target: object, propertyKey: string | symbol, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    const CACHE_SYMBOL = Symbol('__cache_' + target.constructor.name + '.' + String(propertyKey) + '__');

    descriptor.value = function (...args: unknown[]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const self = this as any;

      const cacheRecord: Map<string, CacheResult> = (self[CACHE_SYMBOL] ??= new Map());

      const key = '__cache_' + target.constructor.name + '.' + String(propertyKey) + '__';
      const now = Date.now();

      const existing = cacheRecord.get(key);
      if (existing && existing.expiresIn > now) {
        if (type === 'observable') {
          return of(existing.value);
        }

        return existing.value;
      }

      const result = originalMethod.apply(self, args);

      if (type === 'observable') {
        return (result as Observable<unknown>).pipe(
          tap(
            value => {
              cacheRecord.set(key, { value, expiresIn: ttlMs + now });
            },
            () => {
              cacheRecord.delete(key);
            }
          )
        );
      }

      cacheRecord.set(key, { value: result, expiresIn: ttlMs + now });
      return result;
    };
  };
}
